import React, { useState, useEffect } from 'react';
import { getBaseUrl } from '../../../../shared/utils/apiConfig';
import { Building2, Users, Activity, AlertCircle, CheckCircle2, XCircle } from 'lucide-react';

/**
 * All Tenants - Platform Admin
 * 
 * Following 00-READ-THIS-FIRST:
 * - SINGLE SOURCE OF TRUTH: Uses /api/tenants endpoint
 * - NO MOCK DATA: All real backend data
 * - NULL SAFETY: Handles missing data gracefully
 * - PROGRESSIVE COMPLEXITY: Simple display, can enhance later
 */

interface Tenant {
  id: string;
  name: string;
  status: string;
  created_at: string;
  config: Record<string, any>;
  features: Record<string, boolean>;
  rate_limits: Record<string, number>;
}

export const AllTenants: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [filter, setFilter] = useState<'all' | 'active' | 'suspended' | 'archived'>('all');

  useEffect(() => {
    // ✅ EVIDENCE-FIRST: Wrap in try-catch to prevent component crashes
    try {
      fetchTenants();
    } catch (err) {
      console.error('Error in useEffect:', err);
      setError('Failed to initialize tenant list');
      setLoading(false);
    }
  }, []);

  const fetchTenants = async () => {
    try {
      const token = localStorage.getItem('astrapeak_platform_admin_token');
      if (!token) {
        setError('No authentication token found');
        setLoading(false);
        return;
      }

      // ✅ SINGLE SOURCE OF TRUTH: Use platform-admin specific endpoint
      const response = await fetch(`${getBaseUrl()}/api/platform-admin/tenants?limit=1000`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) throw new Error('Failed to fetch tenants');
      
      const data = await response.json();
      // ✅ NULL SAFETY: Backend returns {tenants: [], total: N}
      const tenantList = data.tenants || [];
      setTenants(Array.isArray(tenantList) ? tenantList : []);
      setError(null);
    } catch (err) {
      console.error('Error fetching tenants:', err);
      setError(err instanceof Error ? err.message : 'Failed to load tenants');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'active':
        return 'bg-green-500/20 text-green-400 border-green-500/30';
      case 'suspended':
        return 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30';
      case 'archived':
        return 'bg-gray-500/20 text-gray-400 border-gray-500/30';
      default:
        return 'bg-gray-500/20 text-gray-400 border-gray-500/30';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status.toLowerCase()) {
      case 'active':
        return <CheckCircle2 className="h-4 w-4" />;
      case 'suspended':
        return <AlertCircle className="h-4 w-4" />;
      case 'archived':
        return <XCircle className="h-4 w-4" />;
      default:
        return <Activity className="h-4 w-4" />;
    }
  };

  const filteredTenants = tenants.filter(tenant => {
    if (filter === 'all') return true;
    return tenant.status.toLowerCase() === filter;
  });

  const stats = {
    total: tenants.length,
    active: tenants.filter(t => t.status.toLowerCase() === 'active').length,
    suspended: tenants.filter(t => t.status.toLowerCase() === 'suspended').length,
    archived: tenants.filter(t => t.status.toLowerCase() === 'archived').length,
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="h-8 w-8 animate-spin border-4 border-primary border-t-transparent rounded-full"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4">
          <p className="text-red-400">Error: {error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-semibold text-white mb-2">All Tenants</h1>
        <p className="text-gray-400">Manage and monitor all tenants across the platform</p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-400 text-sm">Total Tenants</p>
              <p className="text-2xl font-semibold text-white mt-1">{stats.total}</p>
            </div>
            <Building2 className="h-8 w-8 text-blue-400" />
          </div>
        </div>

        <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-400 text-sm">Active</p>
              <p className="text-2xl font-semibold text-green-400 mt-1">{stats.active}</p>
            </div>
            <CheckCircle2 className="h-8 w-8 text-green-400" />
          </div>
        </div>

        <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-400 text-sm">Suspended</p>
              <p className="text-2xl font-semibold text-yellow-400 mt-1">{stats.suspended}</p>
            </div>
            <AlertCircle className="h-8 w-8 text-yellow-400" />
          </div>
        </div>

        <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-400 text-sm">Archived</p>
              <p className="text-2xl font-semibold text-gray-400 mt-1">{stats.archived}</p>
            </div>
            <XCircle className="h-8 w-8 text-gray-400" />
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="flex gap-2">
        {(['all', 'active', 'suspended', 'archived'] as const).map((filterOption) => (
          <button
            key={filterOption}
            onClick={() => setFilter(filterOption)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              filter === filterOption
                ? 'bg-primary text-white'
                : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
            }`}
          >
            {filterOption.charAt(0).toUpperCase() + filterOption.slice(1)}
          </button>
        ))}
      </div>

      {/* Tenants Table */}
      <div className="bg-gray-800 rounded-lg border border-gray-700 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-900/50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                  Tenant
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                  Created
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                  Features
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-400 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-700">
              {filteredTenants.map((tenant) => (
                <tr key={tenant.id} className="hover:bg-gray-700/50 transition-colors">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <Building2 className="h-5 w-5 text-gray-400 mr-3" />
                      <div>
                        <div className="text-sm font-medium text-white">{tenant.name}</div>
                        <div className="text-xs text-gray-400">{tenant.id}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ${getStatusColor(tenant.status)}`}>
                      {getStatusIcon(tenant.status)}
                      {tenant.status}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-400">
                    {new Date(tenant.created_at).toLocaleDateString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex gap-1">
                      {tenant.features?.ml_enabled && (
                        <span className="px-2 py-1 bg-blue-500/20 text-blue-400 text-xs rounded">ML</span>
                      )}
                      {tenant.features?.advanced_correlation && (
                        <span className="px-2 py-1 bg-purple-500/20 text-purple-400 text-xs rounded">Correlation</span>
                      )}
                      {tenant.features?.custom_integrations && (
                        <span className="px-2 py-1 bg-green-500/20 text-green-400 text-xs rounded">Integrations</span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <button className="text-primary hover:text-primary/80 transition-colors">
                      View Details
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {filteredTenants.length === 0 && (
          <div className="p-8 text-center">
            <Building2 className="h-12 w-12 text-gray-600 mx-auto mb-3" />
            <p className="text-gray-400">No tenants found</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default AllTenants;
